import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react'
import ReactDOM from 'react-dom'
import { useAnalyticsDrilldownLevel1, useAnalyticsDrilldownLevel2 } from 'app/queryHooks'
import { Analytics, AnalyticsEvent, AppData } from 'app/types'
import {
    Button, DropdownMenu, IconButton, Spinner,
} from 'components'
import { AnalyticsChartPointSelection } from '../Analytics/AnalyticsChartView'

const DRILLDOWN_WIDTH_STORAGE_KEY = 'analyticsDrilldownDrawerWidth'
const DRILLDOWN_SOURCE_CACHE_KEY = 'analyticsDrilldownSources'
const DRILLDOWN_MIN_WIDTH = 420
const DRILLDOWN_MAX_WIDTH = 1000

type DrilldownSortBy = 'session' | 'source' | 'identity' | 'hubspot' | 'pages' | 'registration' | 'booking' | 'activity'

function clampDrawerWidth(width: number): number {
    const viewportMax = Math.max(DRILLDOWN_MIN_WIDTH, window.innerWidth - 40)
    return Math.max(DRILLDOWN_MIN_WIDTH, Math.min(width, Math.min(DRILLDOWN_MAX_WIDTH, viewportMax)))
}

interface AnalyticsDrilldownPanelProps {
    app?: AppData
    type?: Analytics
    aggregation: string
    filters?: string
    point: AnalyticsChartPointSelection | null
    onClose: () => void
}

function extractFilterValue(filters: string | undefined, key: string): string | undefined {
    if (!filters) {
        return undefined
    }
    const tokens = filters.split(',')
    for (let i = 0; i < tokens.length - 1; i += 2) {
        if (tokens[i] === key) {
            return tokens[i + 1]
        }
    }
    return undefined
}

function formatDateShort(dateStr: string | null | undefined): string {
    if (!dateStr) {
        return '—'
    }
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) {
        return dateStr
    }
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDateTime(dateStr: string | null | undefined): string {
    if (!dateStr) {
        return '—'
    }
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) {
        return dateStr
    }
    return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
}

function formatCount(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '0'
    }
    return value.toLocaleString()
}

function formatDurationMinutes(totalMinutes: number | null | undefined): string {
    if (!totalMinutes || Number.isNaN(totalMinutes)) {
        return '—'
    }

    const minutes = Math.max(0, Math.round(totalMinutes))
    if (minutes < 60) {
        return `${minutes}m`
    }

    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60

    if (hours < 24) {
        return `${hours}h ${remainingMinutes}m`
    }

    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    return `${days}d ${remainingHours}h`
}

function SourceBadge({ source }: { source: string | null | undefined }) {
    const label = source || 'anonymous'
    const cls = `drilldown-source-badge drilldown-source-badge--${label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`
    return <span className={cls}>{label}</span>
}

function FlagBadge({ flag, label }: { flag: boolean, label: string }) {
    const falseLabel = label === 'Linked' ? 'Unlinked' : `No ${label}`

    return (
        <span className={`drilldown-flag-badge ${flag ? 'drilldown-flag-badge--yes' : 'drilldown-flag-badge--no'}`}>
            {flag ? label : falseLabel}
        </span>
    )
}

export function getLevel1Identity(row: Record<string, unknown>) {
    const userName = (((row.userName as string | null)
        || (row.user_name as string | null)
        || '')).trim()
    const email = (((row.displayEmail as string | null)
        || (row.display_email as string | null)
        || (row.userEmail as string | null)
        || (row.user_email as string | null)
        || (row.registrationEmail as string | null)
        || (row.registration_email as string | null)
        || '')).trim()

    return {
        userName: userName || 'Unknown user',
        email: email || 'No email',
        hasUserName: Boolean(userName),
        hasEmail: Boolean(email),
    }
}

function getLevel1Field<T>(row: Record<string, unknown>, camelKey: string, snakeKey: string): T | undefined {
    if (row[camelKey] !== undefined) {
        return row[camelKey] as T
    }
    return row[snakeKey] as T
}

function IdentityCell({ row }: { row: Record<string, unknown> }) {
    const identity = getLevel1Identity(row)

    return (
        <div className="drilldown-identity-cell">
            <div className={`drilldown-identity-cell__name${identity.hasUserName ? '' : ' drilldown-identity-cell__name--fallback'}`}>
                {identity.userName}
            </div>
            <div className={`drilldown-identity-cell__email${identity.hasEmail ? '' : ' drilldown-identity-cell__email--fallback'}`}>
                {identity.email}
            </div>
        </div>
    )
}

function getField<T>(obj: Record<string, unknown> | null | undefined, camelKey: string, snakeKey: string): T | undefined {
    if (!obj) {
        return undefined
    }
    if (obj[camelKey] !== undefined) {
        return obj[camelKey] as T
    }
    return obj[snakeKey] as T
}

function formatReadableLabel(value: string): string {
    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
}

function getEventLabel(event: Record<string, unknown>): string {
    const eventType = getField<string>(event, 'eventType', 'event_type')
    if (eventType) {
        return eventType.includes(' ') ? eventType : formatReadableLabel(eventType)
    }

    const eventTypeId = getField<number | string>(event, 'analyticsEventTypeId', 'analytics_event_type_id')
    if (eventTypeId === undefined || eventTypeId === null || eventTypeId === '') {
        return 'event'
    }

    const eventKey = AnalyticsEvent[Number(eventTypeId) as AnalyticsEvent]
    return eventKey ? formatReadableLabel(eventKey) : `event ${String(eventTypeId)}`
}

/* ─── Level-2 sub-components ─────────────────────────────────────────────── */

function ProfileCard({ profile }: { profile: Record<string, unknown> }) {
    if (!profile || Object.keys(profile).length === 0) {
        return <div className="drilldown-empty">No profile data</div>
    }
    const email = getField<string>(profile, 'userEmail', 'user_email')
        || getField<string>(profile, 'registrationEmail', 'registration_email')
    const fullName = getField<string>(profile, 'userName', 'user_name')
    const firstName = getField<string>(profile, 'firstName', 'first_name')
    const lastName = getField<string>(profile, 'lastName', 'last_name')
    const name = fullName || ((firstName || lastName) ? `${firstName || ''} ${lastName || ''}`.trim() : undefined)
    const source = getField<string>(profile, 'latestSource', 'latest_source')
        || getField<string>(profile, 'utmSource', 'utm_source')
    const firstSeen = getField<string>(profile, 'firstSeen', 'first_seen')
        || getField<string>(profile, 'dateVisited', 'date_visited')
    const lastSeen = getField<string>(profile, 'lastSeen', 'last_seen')
        || getField<string>(profile, 'dateVisited', 'date_visited')
    const sessionCount = getField<number>(profile, 'sessionCount', 'session_count')

    return (
        <div className="drilldown-profile-card">
            <div className="drilldown-profile-card__avatar">
                <i className="fas fa-user-circle" />
            </div>
            <div className="drilldown-profile-card__info">
                {name && <div className="drilldown-profile-card__name">{name}</div>}
                {email && <div className="drilldown-profile-card__email">{email}</div>}
                <div className="drilldown-profile-card__meta">
                    {source && <SourceBadge source={source} />}
                    {sessionCount !== undefined && (
                        <span className="drilldown-meta-item">
                            <i className="fas fa-layer-group" /> {sessionCount} session{sessionCount !== 1 ? 's' : ''}
                        </span>
                    )}
                    {firstSeen && (
                        <span className="drilldown-meta-item">
                            <i className="fas fa-clock" /> First {formatDateShort(firstSeen)}
                        </span>
                    )}
                    {lastSeen && (
                        <span className="drilldown-meta-item">
                            <i className="fas fa-history" /> Last {formatDateShort(lastSeen)}
                        </span>
                    )}
                </div>
            </div>
        </div>
    )
}

function SessionsTable({ sessions }: { sessions: Record<string, unknown>[] }) {
    if (!sessions || sessions.length === 0) {
        return <div className="drilldown-empty">No sessions found</div>
    }
    return (
        <div className="admin-table scrollable">
            <table className="drilldown-sessions-table">
                <thead>
                    <tr>
                        <th className="drilldown-sessions-table__col-id">Session ID</th>
                        <th className="drilldown-sessions-table__col-source">Source</th>
                        <th className="drilldown-sessions-table__col-date">Date</th>
                        <th className="drilldown-sessions-table__col-metric">Pages</th>
                        <th className="drilldown-sessions-table__col-metric">Duration</th>
                    </tr>
                </thead>
                <tbody>
                    {sessions.map((s) => {
                        const sessionId = getField<string>(s, 'sessionId', 'session_id')
                        const totalDuration = getField<number>(s, 'totalDuration', 'total_duration')
                        const durationMinutes = getField<number>(s, 'durationMinutes', 'duration_minutes')
                        const calculatedDurationMinutes = durationMinutes
                            ?? (totalDuration ? Math.round(totalDuration / 60) : undefined)
                        const pageCount = getField<number>(s, 'pageEvents', 'page_events')
                            || getField<number>(s, 'pageCount', 'page_count')
                            || 0

                        return (
                            <tr key={sessionId as string}>
                                <td className="drilldown-session-id">{sessionId ? `${sessionId.slice(0, 12)}…` : '—'}</td>
                                <td><SourceBadge source={getField<string>(s, 'utmSource', 'utm_source')} /></td>
                                <td>{formatDateShort(getField<string>(s, 'dateVisited', 'date_visited'))}</td>
                                <td className="drilldown-metric-cell">{formatCount(pageCount)}</td>
                                <td
                                    className="drilldown-metric-cell"
                                    title={calculatedDurationMinutes ? `${formatCount(calculatedDurationMinutes)} minutes` : 'No duration'}
                                >
                                    {formatDurationMinutes(calculatedDurationMinutes)}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

function PageTimeline({ pages }: { pages: Record<string, unknown>[] }) {
    if (!pages || pages.length === 0) {
        return <div className="drilldown-empty">No page visits</div>
    }

    type CollapsedRow = {
        p: Record<string, unknown>
        totalDuration: number
        count: number
    }

    const getPageLabel = (p: Record<string, unknown>) => getField<string>(p, 'pageName', 'page_name')
        || getField<string>(p, 'pageLink', 'page_link')
        || 'Unknown page'

    const getDataLinkLabel = (p: Record<string, unknown>) => getField<string>(p, 'dataLink', 'data_link')

    const collapsed = pages.reduce<CollapsedRow[]>((acc, p) => {
        const pageLabel = getPageLabel(p)
        const dataLinkLabel = getDataLinkLabel(p) || ''
        const pageKey = `${pageLabel}::${dataLinkLabel}`
        const duration = getField<number>(p, 'durationSeconds', 'duration_seconds') || getField<number>(p, 'duration', 'duration') || 0
        const prev = acc[acc.length - 1]
        if (prev) {
            const prevKey = `${getPageLabel(prev.p)}::${getDataLinkLabel(prev.p) || ''}`
            if (prevKey === pageKey) {
                prev.totalDuration += duration
                prev.count += 1
                return acc
            }
        }
        acc.push({ p, totalDuration: duration, count: 1 })
        return acc
    }, [])

    const maxDuration = 300

    return (
        <div className="drilldown-page-timeline">
            {collapsed.map(({ p, totalDuration }, i) => {
                const pct = totalDuration / maxDuration
                const widthPct = totalDuration ? Math.min(pct * 100, 100) : 0
                const label = getPageLabel(p)
                const dataLink = getDataLinkLabel(p)
                const nameTitle = dataLink ? `${label} / ${dataLink}` : label
                return (
                    <div key={i} className="drilldown-page-row">
                        <div className="drilldown-page-row__name" title={nameTitle}>
                            <span className="drilldown-page-row__primary">{label}</span>
                            {dataLink && <span className="drilldown-page-row__data-link">/{dataLink}</span>}
                        </div>
                        <div className="drilldown-page-row__bar-wrap">
                            <div className="drilldown-page-row__bar" style={{ width: `${widthPct}%` }} />
                        </div>
                        <div className="drilldown-page-row__duration">
                            {totalDuration >= 3600
                                ? `${(totalDuration / 3600).toFixed(1)}h`
                                : totalDuration >= 60
                                    ? `${Math.round(totalDuration / 60)}m`
                                    : totalDuration > 0
                                        ? `${totalDuration}s`
                                        : '—'}
                        </div>
                        <div className="drilldown-page-row__date">
                            {formatDateTime(getField<string>(p, 'dateVisited', 'date_visited'))}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

function FloorplanGrid({ floorplans }: { floorplans: Record<string, unknown>[] }) {
    if (!floorplans || floorplans.length === 0) {
        return <div className="drilldown-empty">No floorplan interactions</div>
    }
    return (
        <div className="drilldown-floorplan-grid">
            {floorplans.map((fp, i) => (
                <div key={i} className="drilldown-floorplan-tile">
                    <div className="drilldown-floorplan-tile__name">{getField<string>(fp, 'floorplanName', 'floorplan_name') || 'Unknown'}</div>
                    <div className="drilldown-floorplan-tile__meta">
                        {(getField<boolean>(fp, 'isFavourite', 'is_favourite') || getField<boolean>(fp, 'favourite', 'favourite')) ? <span className="drilldown-star"><i className="fas fa-star" /></span> : null}
                        <span>{getField<number>(fp, 'viewCount', 'view_count') || 1}×</span>
                        <span>{formatDateShort(getField<string>(fp, 'lastViewed', 'last_viewed') || getField<string>(fp, 'dateVisited', 'date_visited'))}</span>
                    </div>
                </div>
            ))}
        </div>
    )
}

function EventsList({ events }: { events: Record<string, unknown>[] }) {
    if (!events || events.length === 0) {
        return <div className="drilldown-empty">No events</div>
    }
    return (
        <div className="drilldown-events-list">
            {events.map((ev, i) => (
                <div key={i} className="drilldown-event-row">
                    <div className="drilldown-event-row__icon">
                        <i className="fas fa-bolt" />
                    </div>
                    <div className="drilldown-event-row__label">{getEventLabel(ev)}</div>
                    <div className="drilldown-event-row__date">{formatDateTime(getField<string>(ev, 'dateSent', 'date_sent') || getField<string>(ev, 'createdAt', 'created_at'))}</div>
                </div>
            ))}
        </div>
    )
}

function HubSpotSection({ hubspot }: { hubspot: Record<string, unknown> | null | undefined }) {
    if (!hubspot || Object.keys(hubspot).length === 0) {
        return <div className="drilldown-empty">No HubSpot data</div>
    }
    const meetings = (getField<Record<string, unknown>[]>(hubspot, 'meetings', 'meetings')) || []
    const contact = getField<Record<string, unknown>>(hubspot, 'contact', 'contact')
    const status = getField<string>(hubspot, 'status', 'status')

    if (!contact && meetings.length === 0) {
        if (status === 'missing_email') {
            return <div className="drilldown-empty">No HubSpot matchable email found</div>
        }
        if (status === 'meetings_cache_missing') {
            return <div className="drilldown-empty">HubSpot cache unavailable</div>
        }
        return <div className="drilldown-empty">No HubSpot data</div>
    }

    const firstName = getField<string>(contact, 'firstName', 'firstname') || ''
    const lastName = getField<string>(contact, 'lastName', 'lastname') || ''
    const hsObjectId = getField<string>(contact, 'hsObjectId', 'hs_object_id')

    // Contact object exists but contains only a raw hubspotutk token with no resolved fields
    const hasContactFields = Boolean(
        firstName || lastName
        || getField<string>(contact, 'email', 'email')
        || getField<string>(contact, 'phone', 'phone')
        || getField<string>(contact, 'company', 'company')
        || (hsObjectId && /^\d+$/.test(hsObjectId)),
    )
    if (!hasContactFields && meetings.length === 0) {
        return <div className="drilldown-empty">HubSpot contact pending resolution</div>
    }
    const fullName = [firstName, lastName].filter(Boolean).join(' ')
    const email = getField<string>(contact, 'email', 'email')
    const phone = getField<string>(contact, 'phone', 'phone')
    const company = getField<string>(contact, 'company', 'company')
    const communityOfInterest = getField<string>(contact, 'communityOfInterest', 'community_of_interest')
    const initialSource = getField<string>(contact, 'initialSource', 'initial_source')
    const utmSource = getField<string>(contact, 'utmSource', 'utm_source')
    const hsFirstUrl = getField<string>(contact, 'hsFirstUrl', 'hs_first_url')

    return (
        <div className="drilldown-hubspot-section">
            {contact && (
                <div className="drilldown-hubspot-contact">
                    {fullName && (
                        <div className="drilldown-hubspot-row drilldown-hubspot-row--name">
                            <i className="fas fa-user" />
                            <span>{fullName}</span>
                        </div>
                    )}
                    {email && (
                        <div className="drilldown-hubspot-row">
                            <i className="fas fa-envelope" />
                            <span>{email}</span>
                        </div>
                    )}
                    {phone && (
                        <div className="drilldown-hubspot-row">
                            <i className="fas fa-phone" />
                            <span>{phone}</span>
                        </div>
                    )}
                    {company && (
                        <div className="drilldown-hubspot-row">
                            <i className="fas fa-building" />
                            <span>{company}</span>
                        </div>
                    )}
                    {communityOfInterest && (
                        <div className="drilldown-hubspot-row">
                            <i className="fas fa-map-marker-alt" />
                            <span>{communityOfInterest}</span>
                        </div>
                    )}
                    {(initialSource || utmSource) && (
                        <div className="drilldown-hubspot-row">
                            <i className="fas fa-funnel-dollar" />
                            <span>{initialSource || utmSource}</span>
                        </div>
                    )}
                    {hsFirstUrl && (
                        <div className="drilldown-hubspot-row drilldown-hubspot-row--url">
                            <i className="fas fa-link" />
                            <span title={hsFirstUrl}>{hsFirstUrl}</span>
                        </div>
                    )}
                    {hsObjectId && /^\d+$/.test(hsObjectId) && (
                        <div className="drilldown-hubspot-row drilldown-hubspot-row--id">
                            <i className="fas fa-id-badge" />
                            <span className="drilldown-hubspot-id">{hsObjectId}</span>
                        </div>
                    )}
                </div>
            )}
            {meetings && meetings.length > 0 && (
                <div className="drilldown-meetings-list">
                    {meetings.map((m, i) => (
                        <div key={i} className="drilldown-meeting-row">
                            <i className="fas fa-calendar-check" />
                            <div className="drilldown-meeting-row__title">{getField<string>(m, 'title', 'title') || 'Meeting'}</div>
                            <div className="drilldown-meeting-row__date">{formatDateTime(getField<string>(m, 'startTime', 'start_time') || getField<string>(m, 'createdAt', 'created_at'))}</div>
                            {getField<string>(m, 'status', 'status') && <span className="drilldown-meeting-row__status">{getField<string>(m, 'status', 'status')}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

/* ─── Main panel ─────────────────────────────────────────────────────────── */

export function AnalyticsDrilldownPanel(props: AnalyticsDrilldownPanelProps) {
    const {
        app,
        type,
        aggregation,
        filters,
        point,
        onClose,
    } = props

    const [page, setPage] = useState(0)
    const [selectedIdentity, setSelectedIdentity] = useState<{ sessionId?: string, userId?: string, hubspotId?: string } | null>(null)
    const [includeHubspot, setIncludeHubspot] = useState(true)
    const [visible, setVisible] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [sourceFilters, setSourceFilters] = useState<string[]>([])
    const [linkedFilter, setLinkedFilter] = useState<'all' | 'linked' | 'unlinked'>('all')
    const [registrationFilter, setRegistrationFilter] = useState<'all' | 'yes' | 'no'>('all')
    const [bookingFilter, setBookingFilter] = useState<'all' | 'yes' | 'no'>('all')
    const [hubspotFilter, setHubspotFilter] = useState<'all' | 'yes' | 'no'>('all')
    const [forceNoCache, setForceNoCache] = useState(false)
    const [sortBy, setSortBy] = useState<DrilldownSortBy>('activity')
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
    const [sourceOptions, setSourceOptions] = useState<string[]>([])
    const [drawerWidth, setDrawerWidth] = useState(() => {
        if (typeof window === 'undefined') {
            return 540
        }
        try {
            const stored = window.localStorage.getItem(DRILLDOWN_WIDTH_STORAGE_KEY)
            if (!stored) {
                return 540
            }
            const parsed = Number(stored)
            return Number.isFinite(parsed) ? clampDrawerWidth(parsed) : 540
        } catch {
            return 540
        }
    })
    const [isResizing, setIsResizing] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [restoreDrawerWidth, setRestoreDrawerWidth] = useState<number | null>(null)

    // Animate in on mount
    useEffect(() => {
        const raf = requestAnimationFrame(() => setVisible(true))
        return () => cancelAnimationFrame(raf)
    }, [])

    // Close on Escape key
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose()
        }
    }, [onClose])

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setSearch(searchInput.trim())
            setPage(0)
        }, 250)

        return () => window.clearTimeout(timeout)
    }, [searchInput])

    useEffect(() => {
        if (!isResizing) {
            return
        }

        const onMouseMove = (e: MouseEvent) => {
            setDrawerWidth(clampDrawerWidth(window.innerWidth - e.clientX))
        }

        const onMouseUp = () => setIsResizing(false)

        document.body.classList.add('drilldown-resizing')
        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)

        return () => {
            document.body.classList.remove('drilldown-resizing')
            window.removeEventListener('mousemove', onMouseMove)
            window.removeEventListener('mouseup', onMouseUp)
        }
    }, [isResizing])

    useEffect(() => {
        const onWindowResize = () => {
            setDrawerWidth((current) => clampDrawerWidth(current))
        }

        window.addEventListener('resize', onWindowResize)
        return () => window.removeEventListener('resize', onWindowResize)
    }, [])

    useEffect(() => {
        try {
            window.localStorage.setItem(DRILLDOWN_WIDTH_STORAGE_KEY, String(drawerWidth))
        } catch {
            // no-op: localStorage may be unavailable
        }
    }, [drawerWidth])

    // Reset level-2 and page when the chart point changes
    useEffect(() => {
        setSelectedIdentity(null)
        setIncludeHubspot(true)
        setSearchInput('')
        setSearch('')
        setSourceFilters([])
        setLinkedFilter('all')
        setRegistrationFilter('all')
        setBookingFilter('all')
        setHubspotFilter('all')
        setForceNoCache(false)
        setSortBy('activity')
        setSortDir('desc')
        setPage(0)
    }, [point])

    useEffect(() => {
        const appId = app?.meta?.id || 'global'
        try {
            const raw = window.localStorage.getItem(`${DRILLDOWN_SOURCE_CACHE_KEY}:${appId}`)
            if (!raw) {
                setSourceOptions([])
                return
            }
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed)) {
                setSourceOptions(parsed.filter((item) => typeof item === 'string'))
            }
        } catch {
            setSourceOptions([])
        }
    }, [app?.meta?.id])

    const from = useMemo(() => extractFilterValue(filters, 'from'), [filters])
    const to = useMemo(() => extractFilterValue(filters, 'to'), [filters])

    const level1Request = useMemo(() => {
        if (!point || !aggregation || !type) {
            return null
        }
        const row = (point.row || {}) as Record<string, unknown>
        const sourceMode = ((row.sourceMode as string) || (row.source_mode as string) || '').toLowerCase()
        const chartStage = (row.stage as string) || (row.stageLabel as string) || ''
        return {
            app,
            type,
            category: type,
            metric: aggregation,
            dimensionKey: point.labelKey,
            dimensionValue: point.labelValue,
            sourceMode: sourceMode || undefined,
            chartStage: chartStage || undefined,
            filters,
            from,
            to,
            search,
            sourceFilter: sourceFilters,
            linkedFilter,
            registrationFilter,
            bookingFilter,
            hubspotFilter,
            nocache: forceNoCache,
            sortBy,
            sortDir,
            count: 50,
            page,
        }
    }, [point, aggregation, type, app, filters, from, to, search, sourceFilters, linkedFilter, registrationFilter, bookingFilter, hubspotFilter, forceNoCache, sortBy, sortDir, page])

    const level1 = useAnalyticsDrilldownLevel1({
        requestArgs: level1Request,
        enabled: Boolean(level1Request),
    })

    const level2Request = useMemo(() => {
        if (!selectedIdentity) {
            return null
        }
        const isHubspotOnlyIdentity = Boolean(selectedIdentity.hubspotId && !selectedIdentity.sessionId && !selectedIdentity.userId)
        return {
            app,
            sessionId: selectedIdentity.sessionId,
            userId: selectedIdentity.userId,
            hubspotId: selectedIdentity.hubspotId,
            includeHubspot,
            nocache: forceNoCache || (includeHubspot && isHubspotOnlyIdentity),
        }
    }, [selectedIdentity, includeHubspot, forceNoCache, app])

    const level2 = useAnalyticsDrilldownLevel2({
        requestArgs: level2Request,
        enabled: Boolean(level2Request),
    })

    useEffect(() => {
        const fromSummary = (level1.data as any)?.summary?.availableSources
        const fromRows = ((level1.data as any)?.rows || [])
            .map((row) => (getLevel1Field<string>(row as Record<string, unknown>, 'latestSource', 'latest_source') || '').trim())
            .filter((item: string) => Boolean(item))

        const merged = Array.from(new Set([
            ...sourceOptions,
            ...(Array.isArray(fromSummary) ? fromSummary : []),
            ...fromRows,
        ])).sort((a, b) => a.localeCompare(b))

        if (merged.length === sourceOptions.length && merged.every((item, idx) => item === sourceOptions[idx])) {
            return
        }

        setSourceOptions(merged)
        try {
            const appId = app?.meta?.id || 'global'
            window.localStorage.setItem(`${DRILLDOWN_SOURCE_CACHE_KEY}:${appId}`, JSON.stringify(merged.slice(0, 1000)))
        } catch {
            // no-op
        }
    }, [level1.data, app?.meta?.id, sourceOptions])

    if (!point) {
        return null
    }

    const level1Rows = level1.data?.rows || []
    const pagination = level1.data?.pagination
    const hasPrev = page > 0
    const hasNext = (pagination?.page || 0) + 1 < (pagination?.totalPages || 1)
    const level1Loading = level1.isLoading || (level1.isFetching && !level1.data)
    const level1ActionLoading = level1.isFetching && !level1Loading
    const level2Loading = level2.isLoading || (level2.isFetching && !level2.data)
    const level2ActionLoading = level2.isFetching && !level2Loading

    const toggleSort = (nextSortBy: DrilldownSortBy) => {
        if (sortBy === nextSortBy) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
        } else {
            setSortBy(nextSortBy)
            setSortDir(nextSortBy === 'activity' ? 'desc' : 'asc')
        }
        setPage(0)
    }

    const renderSortIcon = (column: DrilldownSortBy) => {
        if (sortBy !== column) {
            return <i className="fas fa-sort" />
        }
        return <i className={`fas ${sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}`} />
    }

    const sourceDropdownItems = useMemo(
        () => sourceOptions.map((source) => ({
            value: source,
            text: source,
        })),
        [sourceOptions],
    )

    const removeSourceFilter = (value: string) => {
        setSourceFilters((current) => current.filter((item) => item !== value))
        setPage(0)
    }

    const toggleFullscreen = () => {
        if (isFullscreen) {
            setIsFullscreen(false)
            if (restoreDrawerWidth !== null) {
                setDrawerWidth(clampDrawerWidth(restoreDrawerWidth))
            }
            setRestoreDrawerWidth(null)
            return
        }

        setRestoreDrawerWidth(drawerWidth)
        setIsFullscreen(true)
        setIsResizing(false)
    }

    const breadcrumb = selectedIdentity
        ? (
            <div className="drilldown-breadcrumb">
                <button className="drilldown-breadcrumb__back" onClick={() => setSelectedIdentity(null)}>
                    <i className="fas fa-chevron-left" />
                    <span>All Sessions</span>
                </button>
                <i className="fas fa-chevron-right drilldown-breadcrumb__sep" />
                <span>{String(point.labelValue)}</span>
                <i className="fas fa-chevron-right drilldown-breadcrumb__sep" />
                <span>User Detail</span>
            </div>
        )
        : (
            <div className="drilldown-breadcrumb">
                <span>All Sessions</span>
                <i className="fas fa-chevron-right drilldown-breadcrumb__sep" />
                <span>{String(point.labelValue)}</span>
            </div>
        )

    const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 600
    const drawerStyle = isMobileViewport
        ? undefined
        : (isFullscreen ? { width: '100vw', maxWidth: '100vw' } : { width: `${drawerWidth}px` })

    const panel = (
        <div className={`analytics-drilldown-overlay${visible ? ' analytics-drilldown-overlay--visible' : ''}`}>
            <div className="analytics-drilldown-overlay__backdrop" onClick={onClose} />
            <div className={`analytics-drilldown-drawer${isFullscreen ? ' analytics-drilldown-drawer--fullscreen' : ''}`} style={drawerStyle}>
                <button
                    className="analytics-drilldown-resize-handle"
                    type="button"
                    onMouseDown={() => {
                        if (!isFullscreen) {
                            setIsResizing(true)
                        }
                    }}
                    aria-label="Resize drilldown panel"
                    title="Drag to resize"
                />
                <div className="analytics-drilldown-header">
                    <div className="analytics-drilldown-header__left">
                        {breadcrumb}
                        <div className="analytics-drilldown-header__meta" aria-label="Drilldown context">
                            <div className="analytics-drilldown-header__meta-item">
                                <span className="analytics-drilldown-header__meta-label">Metric</span>
                                <span className="analytics-drilldown-header__meta-value">{aggregation}</span>
                            </div>
                            <div className="analytics-drilldown-header__meta-item">
                                <span className="analytics-drilldown-header__meta-label">Dimension</span>
                                <span className="analytics-drilldown-header__meta-value">{point.labelKey}</span>
                            </div>
                        </div>
                    </div>
                    <div className="analytics-drilldown-header__actions">
                        <button
                            className="analytics-drilldown-header__toggle"
                            onClick={toggleFullscreen}
                            type="button"
                            aria-label={isFullscreen ? 'Collapse sidebar' : 'Expand sidebar fullscreen'}
                            title={isFullscreen ? 'Collapse sidebar' : 'Expand sidebar fullscreen'}
                        >
                            <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`} />
                        </button>
                        <IconButton icon="fas fa-times" onClick={onClose} alt />
                    </div>
                </div>

                <div className="analytics-drilldown-content">
                    {!selectedIdentity ? (
                        /* ── Level 1 ── */
                        <div className="analytics-drilldown-level1">
                            {level1Loading ? (
                                <Spinner className="drilldown-spinner" showAfter={0}>
                                    <div className="analytics-drilldown-loading-label">Loading sessions...</div>
                                </Spinner>
                            ) : level1.isError ? (
                                <div className={`analytics-drilldown-error ${level1ActionLoading ? 'is-loading' : ''}`}>
                                    <div className="analytics-drilldown-error__message">
                                        <i className="fas fa-exclamation-circle" /> Unable to load sessions.
                                    </div>
                                    <div className="analytics-drilldown-error__actions">
                                        <Button
                                            onClick={() => {
                                                if (level1ActionLoading) {
                                                    return
                                                }
                                                level1.refetch()
                                            }}
                                            small
                                            className={`analytics-drilldown-error__button ${level1ActionLoading ? 'disabled' : ''}`}
                                        >
                                            Retry
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                if (level1ActionLoading) {
                                                    return
                                                }
                                                if (!forceNoCache) {
                                                    setForceNoCache(true)
                                                    setPage(0)
                                                    return
                                                }
                                                setForceNoCache(true)
                                                setPage(0)
                                                level1.refetch()
                                            }}
                                            small
                                            className={`analytics-drilldown-error__button ${level1ActionLoading ? 'disabled' : ''}`}
                                        >
                                            Bypass Cache
                                        </Button>
                                    </div>
                                    {level1ActionLoading && (
                                        <div className="analytics-drilldown-error__loading">
                                            <i className="fas fa-spinner fa-spin" /> Refreshing sessions...
                                        </div>
                                    )}
                                </div>
                            ) : level1Rows.length === 0 ? (
                                <div className={`analytics-drilldown-error ${level1ActionLoading ? 'is-loading' : ''}`}>
                                    <div className="analytics-drilldown-error__message">
                                        <i className="fas fa-info-circle" /> No sessions found for this datapoint.
                                    </div>
                                    <div className="analytics-drilldown-error__actions">
                                        <Button
                                            onClick={() => {
                                                if (level1ActionLoading) {
                                                    return
                                                }
                                                level1.refetch()
                                            }}
                                            small
                                            className={`analytics-drilldown-error__button ${level1ActionLoading ? 'disabled' : ''}`}
                                        >
                                            Retry
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                if (level1ActionLoading) {
                                                    return
                                                }
                                                if (!forceNoCache) {
                                                    setForceNoCache(true)
                                                    setPage(0)
                                                    return
                                                }
                                                setForceNoCache(true)
                                                setPage(0)
                                                level1.refetch()
                                            }}
                                            small
                                            className={`analytics-drilldown-error__button ${level1ActionLoading ? 'disabled' : ''}`}
                                        >
                                            Bypass Cache
                                        </Button>
                                    </div>
                                    {level1ActionLoading && (
                                        <div className="analytics-drilldown-error__loading">
                                            <i className="fas fa-spinner fa-spin" /> Checking fresh data (cache bypass)...
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="analytics-drilldown-controls">
                                        <input
                                            className="analytics-drilldown-controls__search"
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            placeholder="Search session, email, source, HubSpot"
                                        />
                                        <DropdownMenu
                                            className="analytics-drilldown-source-dropdown"
                                            buttonClass="analytics-drilldown-source-dropdown__button"
                                            text={sourceFilters.length > 0 ? `Sources (${sourceFilters.length})` : 'Sources'}
                                            icon="fas fa-filter"
                                            items={sourceDropdownItems}
                                            value={sourceFilters}
                                            onChange={(nextValues) => {
                                                const values = Array.isArray(nextValues)
                                                    ? nextValues.map((value) => String(value))
                                                    : []
                                                setSourceFilters(values)
                                                setPage(0)
                                            }}
                                            multi
                                            search
                                            closeOnSelect={false}
                                        />
                                        <select value={linkedFilter} onChange={(e) => { setLinkedFilter(e.target.value as 'all' | 'linked' | 'unlinked'); setPage(0) }}>
                                            <option value="all">All Link Status</option>
                                            <option value="linked">Linked</option>
                                            <option value="unlinked">Unlinked</option>
                                        </select>
                                        <select value={hubspotFilter} onChange={(e) => { setHubspotFilter(e.target.value as 'all' | 'yes' | 'no'); setPage(0) }}>
                                            <option value="all">HubSpot Any</option>
                                            <option value="yes">HubSpot Yes</option>
                                            <option value="no">HubSpot No</option>
                                        </select>
                                        <select value={registrationFilter} onChange={(e) => { setRegistrationFilter(e.target.value as 'all' | 'yes' | 'no'); setPage(0) }}>
                                            <option value="all">Registration Any</option>
                                            <option value="yes">Registration Yes</option>
                                            <option value="no">Registration No</option>
                                        </select>
                                        <select value={bookingFilter} onChange={(e) => { setBookingFilter(e.target.value as 'all' | 'yes' | 'no'); setPage(0) }}>
                                            <option value="all">Booked Any</option>
                                            <option value="yes">Booked Yes</option>
                                            <option value="no">Booked No</option>
                                        </select>
                                        <Button
                                            small
                                            onClick={() => {
                                                setForceNoCache((current) => !current)
                                                setPage(0)
                                            }}
                                        >
                                            {forceNoCache ? 'No Cache: On' : 'No Cache: Off'}
                                        </Button>
                                        <Button
                                            small
                                            onClick={() => {
                                                setSearchInput('')
                                                setSearch('')
                                                setSourceFilters([])
                                                setLinkedFilter('all')
                                                setHubspotFilter('all')
                                                setRegistrationFilter('all')
                                                setBookingFilter('all')
                                                setForceNoCache(false)
                                                setSortBy('activity')
                                                setSortDir('desc')
                                                setPage(0)
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                    <div className="analytics-drilldown-active-filters" aria-label="Active drilldown filters">
                                        {search !== '' && (
                                            <button className="analytics-drilldown-filter-pill" onClick={() => { setSearchInput(''); setSearch(''); setPage(0) }}>
                                                Search: {search} <i className="fas fa-times" />
                                            </button>
                                        )}
                                        {sourceFilters.map((source) => (
                                            <button key={source} className="analytics-drilldown-filter-pill" onClick={() => removeSourceFilter(source)}>
                                                Source: {source} <i className="fas fa-times" />
                                            </button>
                                        ))}
                                        {linkedFilter !== 'all' && (
                                            <button className="analytics-drilldown-filter-pill" onClick={() => { setLinkedFilter('all'); setPage(0) }}>
                                                Link: {linkedFilter} <i className="fas fa-times" />
                                            </button>
                                        )}
                                        {hubspotFilter !== 'all' && (
                                            <button className="analytics-drilldown-filter-pill" onClick={() => { setHubspotFilter('all'); setPage(0) }}>
                                                HubSpot: {hubspotFilter} <i className="fas fa-times" />
                                            </button>
                                        )}
                                        {registrationFilter !== 'all' && (
                                            <button className="analytics-drilldown-filter-pill" onClick={() => { setRegistrationFilter('all'); setPage(0) }}>
                                                Reg: {registrationFilter} <i className="fas fa-times" />
                                            </button>
                                        )}
                                        {bookingFilter !== 'all' && (
                                            <button className="analytics-drilldown-filter-pill" onClick={() => { setBookingFilter('all'); setPage(0) }}>
                                                Booked: {bookingFilter} <i className="fas fa-times" />
                                            </button>
                                        )}
                                        {forceNoCache && (
                                            <button className="analytics-drilldown-filter-pill" onClick={() => { setForceNoCache(false); setPage(0) }}>
                                                No Cache <i className="fas fa-times" />
                                            </button>
                                        )}
                                    </div>
                                    <div className={`analytics-drilldown-table-wrapper ${level1.isFetching && level1.data ? 'is-loading' : ''}`}>
                                        {level1.isFetching && level1.data && (
                                            <div className="analytics-drilldown-pagination-loading">
                                                <div className="analytics-drilldown-pagination-loading__content">
                                                    <Spinner className="analytics-drilldown-pagination-loading__spinner" showAfter={0}>
                                                        <div>Fetching page...</div>
                                                    </Spinner>
                                                    <Button
                                                        onClick={() => level1.refetch()}
                                                        small
                                                        className="analytics-drilldown-pagination-loading__cancel"
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        <div className="admin-table scrollable show-scroll">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>
                                                            <button className="drilldown-sort-header" onClick={() => toggleSort('session')}>
                                                                <span>Session</span>{renderSortIcon('session')}
                                                            </button>
                                                        </th>
                                                        <th>
                                                            <button className="drilldown-sort-header" onClick={() => toggleSort('source')}>
                                                                <span>Source</span>{renderSortIcon('source')}
                                                            </button>
                                                        </th>
                                                        <th>
                                                            <button className="drilldown-sort-header" onClick={() => toggleSort('identity')}>
                                                                <span>User / Email</span>{renderSortIcon('identity')}
                                                            </button>
                                                        </th>
                                                        <th>
                                                            <button className="drilldown-sort-header" onClick={() => toggleSort('hubspot')}>
                                                                <span>HubSpot</span>{renderSortIcon('hubspot')}
                                                            </button>
                                                        </th>
                                                        <th>
                                                            <button className="drilldown-sort-header" onClick={() => toggleSort('pages')}>
                                                                <span>Pages</span>{renderSortIcon('pages')}
                                                            </button>
                                                        </th>
                                                        <th>
                                                            <button className="drilldown-sort-header" onClick={() => toggleSort('registration')}>
                                                                <span>Reg</span>{renderSortIcon('registration')}
                                                            </button>
                                                        </th>
                                                        <th>
                                                            <button className="drilldown-sort-header" onClick={() => toggleSort('booking')}>
                                                                <span>Booked</span>{renderSortIcon('booking')}
                                                            </button>
                                                        </th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {level1Rows.map((row, idx) => {
                                                        const sessionId = getLevel1Field<string>(row as Record<string, unknown>, 'sessionId', 'session_id')
                                                        const userId = getLevel1Field<string>(row as Record<string, unknown>, 'userId', 'user_id')
                                                        const latestSource = getLevel1Field<string>(row as Record<string, unknown>, 'latestSource', 'latest_source')
                                                        const hubspotId = getLevel1Field<string>(row as Record<string, unknown>, 'hubspotId', 'hubspot_id')
                                                        const pageEvents = getLevel1Field<number>(row as Record<string, unknown>, 'pageEvents', 'page_events')
                                                        const hasRegistration = getLevel1Field<number | string>(row as Record<string, unknown>, 'hasRegistration', 'has_registration')
                                                        const hasBooking = getLevel1Field<number | string>(row as Record<string, unknown>, 'hasBooking', 'has_booking')
                                                        const internalLinkedRaw = getLevel1Field<number | string>(row as Record<string, unknown>, 'internalLinked', 'internal_linked')
                                                        const isLinked = internalLinkedRaw !== undefined
                                                            ? Number(internalLinkedRaw) === 1
                                                            : Boolean(sessionId || userId)
                                                        const canOpenLevel2 = Boolean(sessionId || userId || hubspotId)

                                                        return (
                                                            <tr
                                                                key={sessionId || `row-${idx}`}
                                                                className={canOpenLevel2 ? 'drilldown-row-clickable' : ''}
                                                                onClick={() => {
                                                                    if (!canOpenLevel2) {
                                                                        return
                                                                    }
                                                                    setSelectedIdentity({
                                                                        sessionId,
                                                                        userId: userId || undefined,
                                                                        hubspotId: hubspotId || undefined,
                                                                    })
                                                                }}
                                                            >
                                                                <td className="drilldown-session-id">{sessionId ? `${sessionId.slice(0, 10)}…` : '—'}</td>
                                                                <td><SourceBadge source={latestSource} /></td>
                                                                <td><IdentityCell row={row as Record<string, unknown>} /></td>
                                                                <td>
                                                                    <div className="drilldown-flags">
                                                                        {hubspotId ? (
                                                                            <span className="drilldown-hs-badge">
                                                                                <i className="fas fa-id-badge" />
                                                                                {/^\d+$/.test(hubspotId) ? ` ${hubspotId}` : ' HS'}
                                                                            </span>
                                                                        ) : '—'}
                                                                        <FlagBadge flag={isLinked} label="Linked" />
                                                                    </div>
                                                                </td>
                                                                <td>{pageEvents || 0}</td>
                                                                <td>
                                                                    <FlagBadge flag={Number(hasRegistration) === 1} label="Reg" />
                                                                </td>
                                                                <td>
                                                                    <FlagBadge flag={Number(hasBooking) === 1} label="Booked" />
                                                                </td>
                                                                <td>
                                                                    {canOpenLevel2 ? <i className="fas fa-chevron-right drilldown-row-arrow" /> : '—'}
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="analytics-drilldown-pagination">
                                            <Button small className={!hasPrev ? 'disabled' : ''} onClick={() => setPage(Math.max(page - 1, 0))}>
                                                <i className="fas fa-chevron-left" />
                                            </Button>
                                            <span>Page {(pagination?.page || 0) + 1} of {pagination?.totalPages || 1}</span>
                                            <Button small className={!hasNext ? 'disabled' : ''} onClick={() => setPage(page + 1)}>
                                                <i className="fas fa-chevron-right" />
                                            </Button>
                                        </div>
                                    </div>
                                    {level1.isFetching && (
                                        <div className="analytics-drilldown-subtle-loading">
                                            <i className="fas fa-spinner fa-spin" /> Refreshing results...
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        /* ── Level 2 ── */
                        <div className="analytics-drilldown-level2">
                            <button
                                className="drilldown-level2-back"
                                onClick={() => setSelectedIdentity(null)}
                            >
                                <i className="fas fa-arrow-left" />
                                <span>Back to Sessions</span>
                            </button>
                            {level2Loading ? (
                                <Spinner className="drilldown-spinner" showAfter={0}>
                                    <div className="analytics-drilldown-loading-label">Loading details...</div>
                                </Spinner>
                            ) : level2.isError ? (
                                <div className={`analytics-drilldown-error ${level2ActionLoading ? 'is-loading' : ''}`}>
                                    <div className="analytics-drilldown-error__message">
                                        <i className="fas fa-exclamation-circle" /> Unable to load user detail.
                                    </div>
                                    <div className="analytics-drilldown-error__actions">
                                        <Button
                                            onClick={() => {
                                                if (level2ActionLoading) {
                                                    return
                                                }
                                                level2.refetch()
                                            }}
                                            small
                                            className={`analytics-drilldown-error__button ${level2ActionLoading ? 'disabled' : ''}`}
                                        >
                                            Retry
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                if (level2ActionLoading) {
                                                    return
                                                }
                                                if (!forceNoCache) {
                                                    setForceNoCache(true)
                                                    return
                                                }
                                                setForceNoCache(true)
                                                level2.refetch()
                                            }}
                                            small
                                            className={`analytics-drilldown-error__button ${level2ActionLoading ? 'disabled' : ''}`}
                                        >
                                            Bypass Cache
                                        </Button>
                                    </div>
                                    {level2ActionLoading && (
                                        <div className="analytics-drilldown-error__loading">
                                            <i className="fas fa-spinner fa-spin" /> Refreshing detail...
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="analytics-drilldown-level2-sections">
                                    <section className="drilldown-section">
                                        <div className="drilldown-section__title">
                                            <i className="fas fa-user" /> Profile
                                        </div>
                                        <ProfileCard profile={level2.data?.profile || {}} />
                                    </section>

                                    <section className="drilldown-section">
                                        <div className="drilldown-section__title">
                                            <i className="fas fa-layer-group" /> Sessions
                                        </div>
                                        <SessionsTable sessions={level2.data?.sessions || []} />
                                    </section>

                                    <section className="drilldown-section">
                                        <div className="drilldown-section__title">
                                            <i className="fas fa-map" /> Page Journey
                                        </div>
                                        <PageTimeline pages={level2.data?.pages || []} />
                                    </section>

                                    <section className="drilldown-section">
                                        <div className="drilldown-section__title">
                                            <i className="fas fa-home" /> Floorplans
                                        </div>
                                        <FloorplanGrid floorplans={level2.data?.floorplans || []} />
                                    </section>

                                    <section className="drilldown-section">
                                        <div className="drilldown-section__title">
                                            <i className="fas fa-bolt" /> Events
                                        </div>
                                        <EventsList events={level2.data?.events || []} />
                                    </section>

                                    <section className="drilldown-section">
                                        <div className="drilldown-section__title drilldown-section__title--hubspot">
                                            <i className="fas fa-id-badge" /> HubSpot
                                        </div>
                                        <HubSpotSection hubspot={level2.data?.hubspot} />
                                    </section>
                                    {level2.isFetching && (
                                        <div className="analytics-drilldown-subtle-loading">
                                            <i className="fas fa-spinner fa-spin" /> Refreshing detail...
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )

    return ReactDOM.createPortal(panel, document.body)
}
